import React, { useState, useEffect, useRef } from 'react';
import './ImageExplosion.css'; // นำเข้าไฟล์ CSS
import { db } from '../Firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import SilverState from './Silver-State.png';
import GoldState from './Gold-State.png';
import DiamondState from './Diamond-State.png';
import SilverSound from './Silver-Sound.wav';
import GoldSound from './Gold-Sound.wav';
import DiamondSound from './Diamond-Sound.wav';

// ปรับระยะเวลาการแสดงให้นานขึ้น
const HOLD_DURATION_MS = 3000; // เดิม 1500ms
const CLOSE_DELAY_MS = HOLD_DURATION_MS + 1000; // เดิม ~2000ms (เผื่อเวลา fade)

const ImageExplosion = ({ onAnimationEnd }) => {
  const [isExploding, setIsExploding] = useState(false);
  const [isAnimationFinished, setIsAnimationFinished] = useState(false);
  const [currentStateValue, setCurrentStateValue] = useState(1);
  const [animationStarted, setAnimationStarted] = useState(false);
  const audioRef = useRef(null);
  const lastAudioSrcRef = useRef(null);
  const [showUnmutePrompt, setShowUnmutePrompt] = useState(false);
  const audioStartedRef = useRef(false);
  const stateShowKeyRef = useRef('State-Show');
  const holdTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let userRef = null;
    // timers moved to refs

    const run = async () => {
      try {
        const storedUserStr = localStorage.getItem('loggedInUser');
        if (!storedUserStr) {
          if (onAnimationEnd) onAnimationEnd();
          return;
        }
        const storedUser = JSON.parse(storedUserStr);
        const username = storedUser?.username;
        if (!username) {
          if (onAnimationEnd) onAnimationEnd();
          return;
        }

        userRef = doc(db, 'Competition-Training', username);
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          if (onAnimationEnd) onAnimationEnd();
          return;
        }

        const data = snap.data();
        const candidateKeys = ['State-Show', 'state-show', 'State-show', 'show-state', 'Show-State'];
        const stateShowKey = candidateKeys.find((k) => Object.prototype.hasOwnProperty.call(data, k)) || 'State-Show';
        stateShowKeyRef.current = stateShowKey;
        const shouldShowRaw = data[stateShowKey];
        const shouldShow =
          shouldShowRaw === true ||
          shouldShowRaw === 'true' ||
          shouldShowRaw === 1 ||
          shouldShowRaw === '1' ||
          shouldShowRaw === 'yes' ||
          shouldShowRaw === 'Yes' ||
          shouldShowRaw === 'YES';
        const stateValue = data['state-current'] ?? data['current-state'] ?? data['State-Current'] ?? data['Current-State'] ?? 1;
        if (isMounted) setCurrentStateValue(Number(stateValue) || 1);

        if (!shouldShow) {
          if (onAnimationEnd) onAnimationEnd();
          return;
        }

        // ตรวจสอบเงื่อนไขเพิ่มเติม: ต้องมี state ที่ถูกต้อง
        const validState = Number(stateValue) >= 1 && Number(stateValue) <= 3;
        if (!validState) {
          if (onAnimationEnd) onAnimationEnd();
          return;
        }

        // Try to play audio BEFORE starting animation to increase chance of success
        const src = Number(stateValue) === 3 ? DiamondSound : Number(stateValue) === 2 ? GoldSound : SilverSound;
        lastAudioSrcRef.current = src;
        const tryPlay = async () => {
          try {
            if (audioRef.current) {
              try { audioRef.current.pause(); } catch (_) {}
            }
            const audio = new Audio(src);
            audio.preload = 'auto';
            audio.volume = 1.0;
            audio.muted = false;
            audioRef.current = audio;
            await audio.play();
            audioStartedRef.current = true;
            return true;
          } catch (err) {
            return false;
          }
        };

        const ok = await tryPlay();
        if (!isMounted) return;
        if (ok) {
          setIsExploding(true);
          setAnimationStarted(true);
          holdTimerRef.current = setTimeout(() => {
            if (isMounted) setIsAnimationFinished(true);
          }, HOLD_DURATION_MS);
          fadeTimerRef.current = setTimeout(async () => {
            try {
              if (userRef) {
                await updateDoc(userRef, { [stateShowKey]: false });
              }
            } catch (_) {
            } finally {
              if (onAnimationEnd) onAnimationEnd();
            }
          }, CLOSE_DELAY_MS);
        } else {
          // Show prompt; when user clicks we will play and then start animation
          setIsExploding(true);
          setShowUnmutePrompt(true);
        }
      } catch (e) {
        if (onAnimationEnd) onAnimationEnd();
      }
    };

    run();

    return () => {
      isMounted = false;
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      // อย่าหยุดเสียงใน cleanup เพื่อให้เล่นต่อได้แม้ overlay จะปิดแล้ว
      // ปล่อยให้เบราว์เซอร์จัดการอายุของอ็อบเจ็กต์เสียงเอง
      // หากต้องการหยุด ให้จัดการจากภายนอกตาม UX ที่ต้องการ
    };
  }, [onAnimationEnd]);

  // เล่นเสียงเมื่อเริ่มแสดงภาพตามสถานะ (สำรอง กรณี pre-play ไม่สำเร็จ)
  useEffect(() => {
    if (!isExploding || audioStartedRef.current) return;
    const src = currentStateValue === 3 ? DiamondSound : currentStateValue === 2 ? GoldSound : SilverSound;
    lastAudioSrcRef.current = src;
    try {
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) {}
      }
      const audio = new Audio(src);
      audio.preload = 'auto';
      audio.volume = 1.0;
      audio.muted = false;
      audioRef.current = audio;
      audio.play()
        .then(() => {
          audioStartedRef.current = true;
        })
        .catch((err) => {
          setShowUnmutePrompt(true);
        });
    } catch (_) {
      // เงียบหากเล่นไม่ได้ (เช่น autoplay policy)
    }
  }, [isExploding, currentStateValue]);

  // ถ้าโดนบล็อก autoplay ให้ลองเปิดเสียงอัตโนมัติเมื่อมีการโต้ตอบครั้งแรก
  useEffect(() => {
    if (!showUnmutePrompt) return;
    const unlock = () => {
      handleEnableSound();
    };
    document.addEventListener('pointerdown', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('pointerdown', unlock, { once: true });
      document.removeEventListener('keydown', unlock, { once: true });
      document.removeEventListener('touchstart', unlock, { once: true });
    };
  }, [showUnmutePrompt]);

  const handleEnableSound = () => {
    const src = lastAudioSrcRef.current;
    if (!src) {
      setShowUnmutePrompt(false);
      return;
    }
    try {
      let audio = audioRef.current;
      if (!audio) {
        audio = new Audio(src);
        audio.preload = 'auto';
        audio.volume = 1.0;
        audio.muted = false;
        audioRef.current = audio;
      }
      audio.play().then(async () => {
        audioStartedRef.current = true;
        setShowUnmutePrompt(false);
        setIsExploding(true);
        setAnimationStarted(true);
        // schedule timers and update State-Show=false
        const storedUserStr = localStorage.getItem('loggedInUser');
        const username = storedUserStr ? JSON.parse(storedUserStr)?.username : null;
        const key = stateShowKeyRef.current || 'State-Show';
        holdTimerRef.current = setTimeout(() => {
          setIsAnimationFinished(true);
        }, HOLD_DURATION_MS);
        fadeTimerRef.current = setTimeout(async () => {
          try {
            if (username) {
              await updateDoc(doc(db, 'Competition-Training', username), { [key]: false });
            }
          } catch (err2) {
          } finally {
            if (onAnimationEnd) onAnimationEnd();
          }
        }, CLOSE_DELAY_MS);
      }).catch((err) => {
        // ถ้ายังไม่สำเร็จ ให้คงปุ่มไว้
      });
    } catch (_) {
      // คงปุ่มไว้ให้ผู้ใช้ลองใหม่
    }
  };

  if (!isExploding) {
    return null;
  }

  return (
    <div className={`explosion-container ${isAnimationFinished ? 'fade-out' : ''}`}>
      {animationStarted && (
        <img
          src={currentStateValue === 3 ? DiamondState : currentStateValue === 2 ? GoldState : SilverState}
          alt={currentStateValue === 3 ? 'Diamond State' : currentStateValue === 2 ? 'Gold State' : 'Silver State'}
          className="explosion-image"
        />
      )}
      {showUnmutePrompt && (
        <button
          onClick={handleEnableSound}
          style={{
            position: 'absolute',
            bottom: '-56px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 14px',
            background: '#222',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            zIndex: 1001,
          }}
        >
          เปิดเสียง
        </button>
      )}
    </div>
  );
};

export default ImageExplosion;